import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Check } from 'lucide-react';
import React, { JSX, ReactElement, useEffect, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { cn, KeyValue, useOnClickOutside } from '@jobcost-monorepo/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  Input,
  InputProps,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../atoms';
import { CrudID } from '@jobcost-monorepo/crud';
import Spinner from '../../atoms/Spinner';

interface Props extends InputProps {
  name: string;
  items: KeyValue[];
  errorName: string;
  label: string;
  onChangeSearch: (search: string) => void;
  handleGetSingleItem: (id: CrudID) => Promise<KeyValue | undefined | void>;
  loading: boolean;
  hasClearButton?: boolean;
  onBlur?: () => void;
  disabled?: boolean;
  defaultValue?: string;
  width?: string;
  onClearCallback?: () => void;
}

const DropdownWithSearch = ({
  name,
  items,
  errorName,
  label,
  onChangeSearch,
  handleGetSingleItem,
  loading = false,
  hasClearButton = true,
  onBlur,
  disabled = false,
  defaultValue = '',
  width = 'w-full',
  onClearCallback,
  ...rest
}: Props): ReactElement => {
  const { control, watch, clearErrors } = useFormContext();
  const linkedFieldValue = watch(name);
  const {
    field: { value, onChange },
    formState: { errors },
  } = useController({ control, name });
  const { t } = useTranslation('errors');
  const error = errors?.[errorName]?.message ?? '';
  const errorTranslated = t(error as string);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useOnClickOutside(dropdownRef, () => {
    setIsOpen(false);
    setSearchTerm('');
  });

  const handleOpenOptions = (open: boolean): void => {
    if (disabled) return;
    setIsOpen(open);
    open && onChangeSearch('');
  };

  const onClickItem = ({ id, name: fieldName }: KeyValue): void => {
    if (!!error) clearErrors(name);
    onChange(id);
    if (inputRef.current) inputRef.current.value = fieldName;
    setSearchTerm(fieldName ?? '');
    setIsOpen(false);
    onBlur && onBlur();
    setSearchTerm('');
  };

  const handleClearValue = (): void => {
    onChange(null);
    if (inputRef.current) inputRef.current.value = '';
    setSearchTerm('');
    setIsOpen(false);
    onBlur && onBlur();
    onClearCallback && onClearCallback();
  };

  const handleChangeSearchValue = async (value: string): Promise<void> => {
    setSearchTerm(value);
    onChangeSearch(value);
  };

  const handleTextValue = async () => {
    if ((value === undefined || value === null) && inputRef.current) {
      //inputRef.current.value = '';
      return;
    }

    let item = items.find((item) => {
      return item.id === value;
    });

    if (!item) {
      const element = await handleGetSingleItem(value);
      if (element) item = element;
    }

    if (inputRef.current)
      inputRef.current.value = item?.name ?? defaultValue ?? '';
  };

  useEffect(() => {
    handleTextValue().then();
  }, [value]);

  useEffect(() => {
    if (linkedFieldValue === undefined || linkedFieldValue === null) {
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [linkedFieldValue]);

  const getDropdownIcon = (): JSX.Element => {
    if (hasClearButton && !!value && !disabled) {
      return (
        <XMarkIcon
          className="h-5 w-5 text-gray-400"
          onClick={handleClearValue}
        />
      );
    }
    return (
      <ChevronUpDownIcon
        className="h-5 w-5 text-gray-400"
        aria-hidden="true"
        onClick={() => handleOpenOptions(!isOpen)}
      />
    );
  };

  useEffect(() => {
    if (inputRef.current) inputRef.current.value = defaultValue ?? '';
  }, [defaultValue]);

  return (
    <div className={cn(width)} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-white">
          {label}
        </label>
      )}
      <div className="relative mt-1 rounded-md shadow-sm">
        <Popover open={isOpen} onOpenChange={handleOpenOptions}>
          <PopoverTrigger className="w-full cursor-pointer">
            <Input
              id={name}
              ref={inputRef}
              readOnly
              value={inputRef.current?.value ?? defaultValue ?? ''}
              disabled={disabled}
              className={cn(
                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                errorTranslated &&
                  'border-red text-red placeholder-red-300 dark:border-red',
              )}
              inputRightElement={
                <div
                  className={cn(
                    'inset-y-0 right-0 flex items-center',
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                    errorTranslated && 'text-red',
                  )}
                >
                  {getDropdownIcon()}
                </div>
              }
              {...rest}
            />
          </PopoverTrigger>
          <PopoverContent className="p-0 z-50 border rounded-md border-gray-300 shadow-md">
            <Command>
              <CommandInput
                value={searchTerm}
                onValueChange={handleChangeSearchValue}
                autoFocus
              />
              <CommandEmpty>{t('noResult')}</CommandEmpty>
              <CommandGroup
                className="max-h-72 z-50 overflow-y-auto"
                forceMount={true}
              >
                {loading ? (
                  <CommandItem>
                    <div className="flex items-center justify-center w-full">
                      <Spinner className="w-5 h-5 border-2 border-dashed rounded-full animate-spin bg-violet2 dark:bg-transparent" />
                    </div>
                  </CommandItem>
                ) : (
                  <>
                    {items?.map((option) => (
                      <CommandItem
                        key={`${option.name}-${option.id}`}
                        onSelect={() => onClickItem(option)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            value === option.id ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        {t(option.name ?? '')}
                      </CommandItem>
                    ))}
                  </>
                )}
              </CommandGroup>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <p className="h-4 text-xs my-1 ml-1 text-red overflow-hidden" id="error">
        {errorTranslated}
      </p>
    </div>
  );
};

export default DropdownWithSearch;
